// How fast to play back somebody else's turn — the rules half.
//
// **A run's later fronts are mostly spent watching.** Every living side ticks
// on every move and each one animates its move, its factory and its guns — so
// a board with four camps on it plays four full turns of animation between one
// of yours and the next. Each is legible on its own and the wait is the sum,
// which is a cost that grows with exactly the thing a run is built to do.
//
// So an opponent's turn plays faster, and *how much* faster scales with how
// many of them there are: one enemy is a duel and worth watching at something
// near full speed, five is a crowd and the point is to get back to your move.
//
// **The player's own turn is never scaled.** That is the half being sped past
// everything else, and a board that played your move at three times speed
// would be taking away the thing the animation is for.
//
// Moved out of `src/client/ai/render` on day 2 of the client split: the count
// of living enemies is a rules question (`areEnemies`), and `BoardFacts.tempo`
// carries the answer. The camera's lead and the player's speed ladder stayed
// behind — those are the client's.

import type { Side } from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { areEnemies, livingSides } from "src/engine/rules/position";

/** Full speed. What the player's own turn always plays at. */
export const NORMAL = 1;

/** With a single opponent — quicker, but still a fight you are watching. */
export const MIN_TEMPO = 1.5;

/**
 * With a crowd. Three times is about where a move stops reading as a move and
 * starts reading as a cut, so it is a ceiling rather than a point on a line
 * that keeps going.
 */
export const MAX_TEMPO = 3;

/** Where the ramp tops out. Past this the board is a crowd either way. */
export const CROWDED = 5;

/**
 * How fast to play a turn belonging to `mover`.
 *
 * `opponents` is how many living armies are actually against the player —
 * **enemies, not sides**. An ally is another army taking a turn
 * and adding to the wait, but it is one the player is on the side of, and
 * speeding a run up because you gained a friend reads as a penalty for the
 * card.
 *
 * Linear between the two ends rather than stepped, so a front that arrives or
 * is cleared changes the pace by a noticeable amount and never by a jolt.
 */
export function tempoFor(mine: boolean, opponents: number): number {
  if (mine || opponents <= 0) return NORMAL;
  if (opponents >= CROWDED) return MAX_TEMPO;
  const through = (opponents - 1) / (CROWDED - 1);
  return MIN_TEMPO + (MAX_TEMPO - MIN_TEMPO) * through;
}

/**
 * How fast to play back a turn belonging to `mover`.
 *
 * **Runs only.** `state.run` being present is what makes a game a run, and it
 * is also the only mode with an unambiguous "the player" — a two-player game
 * has two of them and a scenario has whoever is sitting down. Everywhere else
 * every turn plays at full speed, which is what they all did before.
 */
export function tempoOf(state: GameState, mover: Side): number {
  const run = state.run;
  if (!run) return NORMAL;

  let opponents = 0;
  for (const side of livingSides(state.position)) {
    if (areEnemies(state.position, run.side, side)) opponents++;
  }
  return tempoFor(!areEnemies(state.position, run.side, mover), opponents);
}
